import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { ApiClient } from '@center-technologies/shared-api-clients';
import type { Context, LocalizedString } from 'types/context';
import type { FilterExpression } from 'types/filters';
import { listContexts } from 'design-system/my-lib';

// ============================================================================
// Interfaces
// ============================================================================

export interface ContextSummary {
  systemName: string;
  name: LocalizedString;
  description?: LocalizedString;
  icon?: string;
  level0PropertyFilters: FilterExpression[];
  allLevelPropertyFilters: FilterExpression[][];
}

export interface ActiveContextState {
  selectedContext: string | null;
  contextInfo: { systemName: string; name: LocalizedString; icon?: string } | null;
  level0Filters: FilterExpression[] | null;
  availableContexts: ContextSummary[];
  loading: boolean;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Extract level-0 property filters from a context definition.
 * Returns only propertyFilters from the first level (level_0).
 */
export function extractLevel0Filters(context: Context): FilterExpression[] {
  const levels = context.definition?.levels;
  if (!levels || levels.length === 0) return [];
  const level0 = levels[0];
  return level0.filters?.propertyFilters ?? [];
}

/**
 * Extract property filters from ALL levels of a context definition.
 * Returns an array of arrays — one FilterExpression[] per level.
 * Empty levels are included to preserve level index correspondence.
 */
export function extractAllLevelFilters(context: Context): FilterExpression[][] {
  const levels = context.definition?.levels;
  if (!levels || levels.length === 0) return [];
  return levels.map(level => level.filters?.propertyFilters ?? []);
}

function contextToSummary(context: Context): ContextSummary {
  return {
    systemName: context.system_name,
    name: context.name,
    description: context.description,
    level0PropertyFilters: extractLevel0Filters(context),
    allLevelPropertyFilters: extractAllLevelFilters(context),
  };
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: ActiveContextState = {
  selectedContext: null,
  contextInfo: null,
  level0Filters: null,
  availableContexts: [],
  loading: false,
};

// ============================================================================
// Async Thunks
// ============================================================================

// Org scope is derived from the token server-side — the thunk takes no organizationId.
export const fetchAvailableContexts = createAsyncThunk<
  ContextSummary[],
  { apiClient: ApiClient },
  { rejectValue: string }
>(
  'activeContext/fetchAvailableContexts',
  async ({ apiClient }, thunkAPI) => {
    try {
      const contexts = await listContexts(apiClient);
      return contexts.map(contextToSummary);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch available contexts';
      return thunkAPI.rejectWithValue(msg);
    }
  }
);

// ============================================================================
// Slice
// ============================================================================

const activeContextSlice = createSlice({
  name: 'activeContext',
  initialState,
  reducers: {
    setActiveContext: (state, action: PayloadAction<string | null>) => {
      const systemName = action.payload;
      if (systemName === null) {
        state.selectedContext = null;
        state.contextInfo = null;
        state.level0Filters = null;
        return;
      }
      const summary = state.availableContexts.find(
        (c) => c.systemName === systemName
      );
      state.selectedContext = systemName;
      state.contextInfo = summary
        ? { systemName: summary.systemName, name: summary.name, icon: summary.icon }
        : null;
      state.level0Filters = summary?.level0PropertyFilters ?? null;
    },
    setAvailableContexts: (state, action: PayloadAction<ContextSummary[]>) => {
      state.availableContexts = action.payload;
    },
    clearContext: (state) => {
      state.selectedContext = null;
      state.contextInfo = null;
      state.level0Filters = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAvailableContexts.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAvailableContexts.fulfilled, (state, action) => {
        state.loading = false;
        state.availableContexts = action.payload;
      })
      .addCase(fetchAvailableContexts.rejected, (state) => {
        state.loading = false;
      });
  },
});

// ============================================================================
// Exports
// ============================================================================

export const { setActiveContext, setAvailableContexts, clearContext } =
  activeContextSlice.actions;

// Selectors
export const selectActiveContext = (state: { activeContext: ActiveContextState }) =>
  state.activeContext.selectedContext;
export const selectActiveContextInfo = (state: { activeContext: ActiveContextState }) =>
  state.activeContext.contextInfo;
export const selectLevel0Filters = (state: { activeContext: ActiveContextState }) =>
  state.activeContext.level0Filters;
export const selectAvailableContexts = (state: { activeContext: ActiveContextState }) =>
  state.activeContext.availableContexts;
export const selectActiveContextLoading = (state: { activeContext: ActiveContextState }) =>
  state.activeContext.loading;

export default activeContextSlice.reducer;
